import { Database, FileSpreadsheet, Truck } from "lucide-react";
import Eyebrow from "@/components/zeblan/Eyebrow";
import Reveal from "@/components/zeblan/Reveal";

const cases = [
  {
    icon: Database,
    title: 'The secure internal "brain"',
    audience: "For HR, Legal & Compliance",
    problem: "Your team spends hours digging through messy shared drives, HR manuals, and old project files for specific answers.",
    solution: "A custom knowledge-retrieval agent trained only on your company's proprietary documents.",
    result: "Instant, accurate answers with citations to the source. Because hosting is local, sensitive data never touches the public internet.",
  },
  {
    icon: FileSpreadsheet,
    title: "The automated quoting assistant",
    audience: "For B2B Sales & Manufacturing",
    problem: "Processing RFQs takes days, cross-referencing incoming specs with inventory, pricing matrices, and past deals.",
    solution: "An agent integrated into your inbox and ERP that reads the request, checks inventory and pricing, and drafts a ready-to-send quote.",
    result: "Turnaround drops from days to minutes. Your sales team stops doing data entry and spends time closing deals.",
  },
  {
    icon: Truck,
    title: "The supply chain data extractor",
    audience: "For Logistics & Operations",
    problem: "Your ops team is drowning in mismatched invoices, shipping delays, and unstructured emails from dozens of vendors.",
    solution: "A deterministic agent reads vendor emails, extracts critical data (tracking numbers, price changes, delays) and updates your central system.",
    result: "Zero manual data entry, zero human-error typos, and a real-time overview of your supply chain.",
  },
];

export default function UseCases() {
  return (
    <section id="use-cases" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>AI in Action</Eyebrow>
          <h2 className="mt-6 text-navy font-extrabold tracking-tightish" style={{ fontSize: "clamp(30px,4vw,46px)" }}>
            What does an AI agent <span className="font-display italic font-medium text-blue">actually do?</span>
          </h2>
          <p className="mt-5 text-slate text-[17px] leading-relaxed">
            Three common ways we help mid-sized companies save time, protect data, and scale output.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map(({ icon: Icon, ...c }, i) => (
            <Reveal key={c.title} delay={i * 80}>
              <article className="h-full bg-white border border-mist rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(10,19,40,0.25)] transition-all duration-300">
                <div className="bg-cloud p-6 border-b border-mist">
                  <div className="h-11 w-11 rounded-lg bg-white border border-mist text-blue grid place-items-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-navy text-[18px] font-bold">{c.title}</h3>
                  <p className="mt-1 text-steel text-[13px]">{c.audience}</p>
                </div>
                <div className="p-6 space-y-5 text-[14px] leading-relaxed">
                  <div>
                    <div className="text-blue text-[11px] font-bold tracking-[0.22em] uppercase">The problem</div>
                    <p className="mt-2 text-slate">{c.problem}</p>
                  </div>
                  <div>
                    <div className="text-blue text-[11px] font-bold tracking-[0.22em] uppercase">Our solution</div>
                    <p className="mt-2 text-slate">{c.solution}</p>
                  </div>
                  <div>
                    <div className="text-teal text-[11px] font-bold tracking-[0.22em] uppercase">The result</div>
                    <p className="mt-2 text-navy font-medium">{c.result}</p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
